/**
 * Names the CRDB shipment a tracking link opens on. When present the link is
 * resolved by id only; `fulfillment_index` and `/track/:index` are legacy.
 */
export const TRACKING_PAGE_SHIPMENT_ID_PARAM = "shipment_id";

const LEGACY_INDEX_QUERY_PARAMS = [
  "fulfillment_index",
  "order_tracking_fulfillment_index",
];
const LEGACY_INDEX_PATH_SEGMENT = /(\/track)\/\d+(?=\/?$)/;

export function findShipmentIndexById(
  shipments: readonly { id: string }[] | undefined,
  shipmentId: string | null | undefined,
): number | undefined {
  if (!shipmentId || !shipments) {
    return undefined;
  }
  const index = shipments.findIndex((shipment) => shipment.id === shipmentId);
  return index === -1 ? undefined : index;
}

/**
 * A shipment id that is gone (cancelled, combined away) selects nothing; the
 * position fallback only applies to links that never carried an id.
 */
export function selectTrackingPageShipmentIndex(
  shipments: readonly { id: string }[],
  {
    shipmentId,
    indexParam,
  }: { shipmentId: string | null | undefined; indexParam: string | undefined },
): number | undefined {
  if (shipmentId) {
    return findShipmentIndexById(shipments, shipmentId);
  }
  const index = indexParam === undefined ? NaN : parseInt(indexParam, 10);
  return Number.isInteger(index) && index >= 0 && index < shipments.length
    ? index
    : undefined;
}

/**
 * Point an order-level tracking link at one shipment, dropping position params
 * so the link is read one way. An unparseable link is returned untouched.
 */
export function withTrackingPageShipmentId(
  trackingPageUrl: string,
  shipmentId: string | bigint,
): string {
  let url: URL;
  try {
    url = new URL(trackingPageUrl);
  } catch {
    return trackingPageUrl;
  }
  url.pathname = url.pathname.replace(LEGACY_INDEX_PATH_SEGMENT, "$1");
  for (const param of LEGACY_INDEX_QUERY_PARAMS) {
    url.searchParams.delete(param);
  }
  url.searchParams.set(TRACKING_PAGE_SHIPMENT_ID_PARAM, shipmentId.toString());
  return url.toString();
}

/**
 * A query string with the shipment selection removed, so a link that names the
 * shipment by path keeps the rest (e.g. the shopper's auth token).
 */
export function withoutTrackingPageShipmentSelection(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(TRACKING_PAGE_SHIPMENT_ID_PARAM);
  for (const param of LEGACY_INDEX_QUERY_PARAMS) {
    params.delete(param);
  }
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}
